/**
 * Charges that wait for the enemy to come over them (10 §2, mission 34: `bombsHoldUntilMidnight` with
 * `detonateUnder`). The standing order says they will; once the column is on the road the timer line counts
 * what is waiting and what has gone up; each detonation is a banner. Pure: text from the rule and the state.
 */

import type { MissionDef, ObjectiveRule, TimedEffect, World } from '../../sim/types';

type HoldRule = Extract<ObjectiveRule, { type: 'bombsHoldUntilMidnight' }>;

const holdRule = (def: MissionDef): HoldRule | undefined =>
  def.objective.rules.find((r): r is HoldRule => r.type === 'bombsHoldUntilMidnight');

/** Who sets them off, as the player is told. */
const WHO: Partial<Record<TimedEffect, string>> = { convoyCrosses: 'THE COLUMN', searchPartyArrives: 'THE SEARCH PARTY' };
const who = (effect: TimedEffect): string => WHO[effect] ?? 'THE ENEMY';

const charges = (n: number): string => `${n} CHARGE${n === 1 ? '' : 'S'}`;

/** The first half of the standing order for a several-target planting mission: what the charges wait for. */
export function chargesOrders(def: MissionDef): string {
  const under = holdRule(def)?.detonateUnder;
  if (under === undefined) return 'CHARGES DO NOT GO OFF WHILE YOU ARE HERE: LAY ONE AT EVERY MARKED TARGET';
  return `LAY A CHARGE AT EVERY MARKED TARGET: THEY GO OFF UNDER ${who(under)} WHEN IT COMES — BE WELL CLEAR BY THEN`;
}

/** Has the event the charges wait for happened? */
function arrived(def: MissionDef, world: World, under: TimedEffect): boolean {
  return world.objective.timed.some((st) => {
    const r = def.objective.rules[st.rule];
    return st.fired && r?.type === 'timedEvent' && r.effect === under;
  });
}

/** "THE COLUMN IS ON THE CHARGES — 2 WENT UP, 1 WAITING": shown once the column is on the road. */
export function chargesLine(def: MissionDef, world: World): string | null {
  const under = holdRule(def)?.detonateUnder;
  if (under === undefined || !arrived(def, world, under)) return null;
  const fired = world.objective.chargesFired;
  const waiting = world.bombs.length;
  if (fired === 0) return waiting > 0 ? `${who(under)} IS ON THE ROAD — ${charges(waiting)} WAITING FOR IT` : null;
  return `${who(under)} IS ON THE CHARGES — ${fired} WENT UP${waiting > 0 ? `, ${waiting} WAITING` : ''}`;
}

/** The banner as each one goes: how many have gone up under them so far. */
export function chargesFiredText(def: MissionDef, fired: number): string | null {
  const under = holdRule(def)?.detonateUnder;
  if (under === undefined || fired <= 0) return null;
  return `${who(under)} IS ON THE CHARGES — ${charges(fired)} WENT UP UNDER IT`;
}
